import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useCallback, useEffect, useRef, useState } from 'react'

import debounceFn from 'lodash.debounce'

/** Google returns at most 5 predictions per request; keep the list the same size. */
export const MAX_PREDICTIONS = 5

const fetchPredictions = (service, request) =>
  new Promise(resolve => {
    service.getPlacePredictions(request, results => resolve(results || []))
  })

/**
 * Use predictions from the preferred country whenever there are any.
 * Only when the input matches nothing there (e.g. "Bengaluru, India") fall
 * back to predictions from anywhere, so other countries stay searchable when
 * the user specifies them.
 */
export const mergePredictions = (preferred, global, limit = MAX_PREDICTIONS) =>
  (preferred.length > 0 ? preferred : global).slice(0, limit)

/**
 * @param {object} params
 * @param {object} params.options - AutocompletionRequest options sent with every request
 * @param {string} [params.preferredCountry] - ISO country code whose suggestions are
 *   shown by default. Suggestions from other countries are shown only when
 *   nothing matches in it, so users can search outside it by being specific
 *   (e.g. "Bengaluru, India").
 * @param {number} params.debounce - debounce delay in ms
 */
export default function usePlacesAutocompleteService({
  options = {},
  preferredCountry,
  debounce = 300,
}) {
  const [placePredictions, setPlacePredictions] = useState([])
  const [isPlacePredsLoading, setIsPlacePredsLoading] = useState(false)
  const [placeInputValue, setPlaceInputValue] = useState(null)
  const [placesAutocompleteService, setPlacesAutocompleteService] =
    useState(null)
  const placesLibrary = useMapsLibrary('places')
  const [placesService, setPlacesService] = useState(null)
  // Incremented per request so responses for outdated input are ignored
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!placesLibrary) return

    setPlacesService(
      new placesLibrary.PlacesService(document.createElement('div'))
    )
    setPlacesAutocompleteService(new placesLibrary.AutocompleteService())
  }, [placesLibrary])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPlacePredictions = useCallback(
    debounceFn(optionsArg => {
      if (!placesAutocompleteService || !optionsArg.input) return

      const requestId = ++requestIdRef.current
      const request = { ...options, ...optionsArg }
      const requests = [fetchPredictions(placesAutocompleteService, request)]
      if (preferredCountry) {
        requests.unshift(
          fetchPredictions(placesAutocompleteService, {
            ...request,
            componentRestrictions: { country: preferredCountry },
          })
        )
      }

      Promise.all(requests).then(([first, second]) => {
        if (requestId !== requestIdRef.current) return
        setIsPlacePredsLoading(false)
        setPlacePredictions(
          preferredCountry ? mergePredictions(first, second) : first
        )
      })
    }, debounce),
    [debounce, debounceFn, placesAutocompleteService, options, preferredCountry]
  )

  return {
    placesService: placesService,
    placesAutocompleteService: placesAutocompleteService,
    placePredictions: placeInputValue ? placePredictions : [],
    isPlacePredictionsLoading: isPlacePredsLoading,
    getPlacePredictions: opt => {
      if (opt.input) {
        setPlaceInputValue(opt.input)
        setIsPlacePredsLoading(true)
        debouncedPlacePredictions(opt)
        return
      }
      // Input cleared: drop any in-flight response
      requestIdRef.current++
      setPlacePredictions([])
      setPlaceInputValue(null)
      debouncedPlacePredictions(opt)
      setIsPlacePredsLoading(false)
    },
  }
}
